import { useTranslation } from 'react-i18next';
import type { Product } from '../../types/product';

interface ProductCardProps {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
}

export function ProductCard({ product, onEdit, onDelete, onCopyLink }: ProductCardProps) {
  const { t } = useTranslation();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(price);
  };

  const getPeriodLabel = () => {
    if (product.period === 'Lifetime') {
      return t('products.period.lifetime');
    }
    const periodLabels: Record<string, string> = {
      Days: t('products.period.days'),
      Weeks: t('products.period.weeks'),
      Months: t('products.period.months'),
      Years: t('products.period.years'),
    };
    return `${product.numOfPeriods} ${periodLabels[product.period] || product.period}`;
  };

  return (
    <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4 hover:bg-surface-card-hover dark:hover:bg-white/15 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Title and Status */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-ink-primary font-medium truncate">{product.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded ${
              product.isActive
                ? 'bg-brand-confirm/30 text-brand-confirm-deep'
                : 'bg-surface-app-elevated text-ink-secondary'
            }`}>
              {product.isActive ? t('products.active') : t('products.inactive')}
            </span>
            {product.isMetered && (
              <span className="text-xs px-2 py-0.5 rounded bg-brand-accent/20 text-brand-accent">
                {t('products.metered')}
              </span>
            )}
          </div>

          {/* Subtitle */}
          {product.subtitle && (
            <p className="text-sm text-ink-secondary truncate">{product.subtitle}</p>
          )}

          {/* Price and Details */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="text-brand-confirm-deep font-medium">
              {formatPrice(product.grossPrice ?? product.price)}
              {product.isMetered && ` / ${product.meteredUnitLabel}`}
            </span>
            {!product.isMetered && (
              <span className="text-ink-muted">
                {t('products.usageCount')}: {product.usage === -1 ? t('products.unlimited') : product.usage}
              </span>
            )}
            <span className="text-ink-muted">
              {getPeriodLabel()}
            </span>
          </div>

          {/* Payment Options */}
          <div className="flex gap-2 mt-2">
            {product.payableInApp && (
              <span className="text-xs px-2 py-0.5 bg-brand-primary/15 text-brand-primary rounded">
                {t('products.payableInApp')}
              </span>
            )}
            {product.payableWithTrainer && (
              <span className="text-xs px-2 py-0.5 bg-brand-primary-light/20 text-brand-primary rounded">
                {t('products.payableWithTrainer')}
              </span>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <p className="text-sm text-ink-muted mt-2 line-clamp-2">
              {product.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onCopyLink}
            className="p-2 text-ink-secondary hover:text-brand-primary hover:bg-surface-card-hover dark:hover:bg-white/10 rounded-lg transition-colors"
            title={t('products.copyLink')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-ink-secondary hover:text-brand-primary hover:bg-surface-card-hover dark:hover:bg-white/10 rounded-lg transition-colors"
            title={t('common.edit')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-ink-secondary hover:text-brand-warm-deep hover:bg-surface-card-hover dark:hover:bg-white/10 rounded-lg transition-colors"
            title={t('common.delete')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
